import React, { useRef, useEffect, useState } from 'react';

// Define props for the ScaleVisualizer component
interface ScaleVisualizerProps {
  scale: number;
  label: string;
  flops: string;
  boxSize: number;
}

// Component to render a single scale visualization using Canvas
function ScaleVisualizer({ scale, label, flops, boxSize }: ScaleVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Constants for drawing
  const fullBoxSide = 8; // Size of the full boxes (width and height)
  const partialBoxSide = 3; // Size of the partial boxes
  const gap = 1.5; // Gap between boxes
  const fullBoxColor = '#374151'; // gray-700
  const partialBoxColor = '#6B7280'; // gray-500
  const hoverColor = 'var(--accent)'; // Use CSS variable for hover
  const backgroundColor = 'transparent'; // Background for empty cells

  // Calculations based on scale
  const fullBoxCount = Math.floor(scale / boxSize);
  const partialBoxCount = scale % boxSize;
  const gridDimension = Math.ceil(Math.sqrt(fullBoxCount));
  const totalGridCells = gridDimension * gridDimension;

  // Calculate canvas dimensions
  const mainGridWidth = gridDimension > 0 ? gridDimension * fullBoxSide + (gridDimension - 1) * gap : 0;
  const mainGridHeight = gridDimension > 0 ? gridDimension * fullBoxSide + (gridDimension - 1) * gap : 0;
  const partialBoxesWidth = partialBoxCount > 0 ? partialBoxCount * partialBoxSide + (partialBoxCount - 1) * gap : 0;
  const partialBoxesHeight = partialBoxCount > 0 ? partialBoxSide : 0;

  // Canvas width is the max of grid width and partial boxes width
  const canvasWidth = Math.max(mainGridWidth, partialBoxesWidth);
  // Canvas height accommodates main grid, gap (if partial boxes exist), and partial boxes
  const canvasHeight = mainGridHeight + (partialBoxCount > 0 ? gap + partialBoxesHeight : 0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Adjust canvas resolution for DPI scaling
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // --- Draw Full Boxes ---
    if (gridDimension > 0) {
        // Use the actual color from the CSS variable for hover
        const computedStyle = getComputedStyle(canvas);
        const actualHoverColor = computedStyle.getPropertyValue('--accent').trim() || '#0070f3'; // Default if not found

        for (let i = 0; i < totalGridCells; i++) {
            const row = Math.floor(i / gridDimension);
            const col = i % gridDimension;
            const x = col * (fullBoxSide + gap);
            const y = row * (fullBoxSide + gap);

            if (i === hoveredIndex) {
                ctx.fillStyle = actualHoverColor;
            } else if (i < fullBoxCount) {
                ctx.fillStyle = fullBoxColor;
            } else {
                ctx.fillStyle = backgroundColor; // Or skip drawing
            }

            if (ctx.fillStyle !== backgroundColor) {
              // Simple square box - could use ctx.roundRect if needed and supported
               ctx.fillRect(x, y, fullBoxSide, fullBoxSide);
            }
        }
    }

    // --- Draw Partial Boxes ---
    if (partialBoxCount > 0) {
      const partialStartY = mainGridHeight + gap;
      // Center the partial boxes horizontally
      const partialStartX = (canvasWidth - partialBoxesWidth) / 2;

      ctx.fillStyle = partialBoxColor;
      for (let i = 0; i < partialBoxCount; i++) {
        const x = partialStartX + i * (partialBoxSide + gap);
        const y = partialStartY;
        ctx.fillRect(x, y, partialBoxSide, partialBoxSide);
      }
    }

  }, [scale, boxSize, hoveredIndex, canvasWidth, canvasHeight, fullBoxColor, partialBoxColor, hoverColor, backgroundColor, fullBoxCount, gridDimension, partialBoxCount, totalGridCells, mainGridHeight, partialBoxesWidth]); // Dependencies for redraw

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (gridDimension === 0) return; // No grid to hover over

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Calculate which cell the mouse is over
    const col = Math.floor(x / (fullBoxSide + gap));
    const row = Math.floor(y / (fullBoxSide + gap));

    // Check if the mouse is within the grid bounds and not in the gaps
    const onGapX = x % (fullBoxSide + gap) > fullBoxSide;
    const onGapY = y % (fullBoxSide + gap) > fullBoxSide;

    if (col >= 0 && col < gridDimension && row >= 0 && row < gridDimension && !onGapX && !onGapY) {
      const index = row * gridDimension + col;
       if (index < totalGridCells) { // Check index is within rendered cells
           setHoveredIndex(index);
           return; // Exit if hover is successful
       }
    }
    // If mouse is not over a valid cell or is in a gap, clear hover state
    setHoveredIndex(null);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  return (
    <div className="flex flex-col items-center flex-1">
      <div className="flex-1 flex flex-col items-center justify-center min-h-[100px]"> {/* Added min-height */}
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="cursor-default"
          // Set style directly for initial render to prevent layout shift
          style={{ width: `${canvasWidth}px`, height: `${canvasHeight}px` }}
          // Width and height attributes are set in useEffect for DPI scaling
        />
      </div>
      <div className="text-xs font-sans text-center mt-3">
        {label} <br />
        {flops}
      </div>
    </div>
  );
}


export default function GPTScale() {
  const relativeScales = [{scale: 1, label: 'GPT-3', flops: ' (3 x 10^23 FLOP)'}, {scale: 70, label: 'GPT-4', flops: ' (2 x 10^25 FLOP)'}, {scale: 13320, label: 'Agent-1', flops: ' (4 x 10^27 FLOP)'}]
  const boxSize = 10; // Each full box represents 10 units

  return (
    <div className="flex flex-col gap-6 mt-10 mb-10">
      {/* Container that changes from row to column at medium breakpoint */}
      <div className="flex flex-col lg:flex-row justify-center lg:justify-between gap-10 w-full">
        {relativeScales.map(({scale, label, flops}) => (
          <ScaleVisualizer
            key={scale}
            scale={scale}
            label={label}
            flops={flops}
            boxSize={boxSize}
          />
        ))}
      </div>
    </div>
  );
}
